/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { memo, useCallback, useEffect, useState, type MemoExoticComponent } from 'react';

import { useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';

import { css, jsx } from '@atlaskit/css';
import SearchIcon from '@atlaskit/icon/core/search';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const searchWrapperStyles = css({
	width: 'min(750px, 93vw)',
	height: '32px',
	margin: 'auto',
	marginTop: token('space.200'),
	display: 'flex',
	alignItems: 'center',
	position: 'relative',
	borderWidth: token('border.width.selected'),
	borderRadius: token('radius.large'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	backgroundColor: token('color.background.input'),
	paddingRight: token('space.100'),
	paddingLeft: token('space.100'),
	'&:focus-within': {
		borderColor: token('color.border.focused'),
	},
});

const inputStyles = css({
	marginLeft: token('space.100'),
	border: 'none',
	outline: 'none',
	backgroundColor: 'transparent',
	flex: 1,
	color: token('color.text'),
	'&::placeholder': {
		color: token('color.text.subtlest'),
	},
});

type RovoConversationSearchPromptsProps = {
	prompts?: string[];
	onSearchChange?: (searchQuery: string, filteredPrompts: string[]) => void;
};

export const RovoConversationSearchPrompts: MemoExoticComponent<
	({ prompts, onSearchChange }: RovoConversationSearchPromptsProps) => JSX.Element
> = memo(({ prompts, onSearchChange }: RovoConversationSearchPromptsProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [searchQuery, setSearchQuery] = useState('');

	const filterPrompts = useCallback(
		(query: string) => {
			if (!prompts) {
				onSearchChange?.(query, []);
				return;
			}

			const filtered = !query.trim()
				? prompts
				: prompts.filter((prompt) => prompt.toLowerCase().includes(query.toLowerCase()));

			onSearchChange?.(query, filtered);
		},
		[prompts, onSearchChange],
	);

	const [debouncedFilterPrompts] = useDebouncedCallback(filterPrompts, 300);

	const handleSearchChange = useCallback(
		(query: string) => {
			debouncedFilterPrompts(query);
		},
		[debouncedFilterPrompts],
	);

	useEffect(() => {
		handleSearchChange(searchQuery);
	}, [searchQuery, handleSearchChange]);

	return (
		<div css={searchWrapperStyles}>
			<SearchIcon label="" size="medium" />
			<input
				css={inputStyles}
				aria-label={formatMessage(messages.searchExamplesLabel)}
				placeholder={formatMessage(messages.searchPlaceholder)}
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.currentTarget.value)}
			/>
		</div>
	);
});
